import { useEffect, useState } from "react";
import { User, Phone, EnvelopeSimple, Shield, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Iter 66.11 — Self-service profile page.
 *
 * Surfaces the current user's identity (read-only email + role) and lets
 * them update their display name + contact phone. These fields flow onto
 * the "Submitted By" footer of every NEW proposal they raise — historical
 * proposals stay frozen on their original `proposal_author` snapshot.
 */
export default function Profile() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({ name: "", phone: "" });
    const [baseline, setBaseline] = useState({ name: "", phone: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) {
            const next = { name: user.name || "", phone: user.phone || "" };
            setForm(next);
            setBaseline(next);
        }
    }, [user]);

    const isDirty = form.name !== baseline.name || form.phone !== baseline.phone;
    const canSave = isDirty && form.name.trim().length > 0 && form.phone.trim().length >= 4 && !saving;

    const onSave = async (e) => {
        e?.preventDefault?.();
        if (!canSave) return;
        setSaving(true);
        try {
            await api.patch("/auth/me/profile", {
                name: form.name.trim(),
                phone: form.phone.trim(),
            });
            await refresh?.();
            setBaseline({ name: form.name.trim(), phone: form.phone.trim() });
            toast.success("Profile updated. Future proposals will use your new contact details.");
        } catch (err) {
            toast.error(formatApiError(err, "Could not save profile"));
        } finally {
            setSaving(false);
        }
    };

    if (!user) {
        return (
            <div className="text-sm text-slate-500 p-5" data-testid="profile-loading">
                Loading profile…
            </div>
        );
    }

    return (
        <div data-testid="profile-page">
            <PageHeader
                title="My Profile"
                description="Your contact details appear on every proposal you raise as 'Submitted By'."
            />
            <div className="max-w-2xl">
                <form onSubmit={onSave} className="reedos-card p-6 space-y-5">
                    {/* Read-only identity strip */}
                    <div className="grid grid-cols-2 gap-4 pb-4 border-b border-slate-100">
                        <div>
                            <Label className="reedos-eyebrow flex items-center gap-1">
                                <EnvelopeSimple size={11} /> Email (login key)
                            </Label>
                            <div className="mt-1 text-sm font-mono text-slate-800" data-testid="profile-email">
                                {user.email}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                                Email is immutable — Super Admin can change it via User Management.
                            </div>
                        </div>
                        <div>
                            <Label className="reedos-eyebrow flex items-center gap-1">
                                <Shield size={11} /> Role
                            </Label>
                            <div className="mt-1 text-sm font-mono uppercase text-slate-800" data-testid="profile-role">
                                {user.role}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">
                                Role changes are admin-controlled.
                            </div>
                        </div>
                    </div>

                    {/* Editable fields */}
                    <div className="space-y-4">
                        <div>
                            <Label className="reedos-eyebrow flex items-center gap-1">
                                <User size={11} /> Display name
                            </Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Karthikeyan R."
                                className="mt-1"
                                data-testid="profile-name-input"
                            />
                        </div>
                        <div>
                            <Label className="reedos-eyebrow flex items-center gap-1">
                                <Phone size={11} /> Contact phone
                            </Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                placeholder="+91 70219 12083"
                                className="mt-1"
                                data-testid="profile-phone-input"
                            />
                            <div className="text-[10px] text-slate-400 mt-1">
                                Shown on every NEW proposal you raise. Historical proposals stay
                                frozen on their original snapshot — they will not auto-rewrite.
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <div className="text-[11px] text-slate-500">
                            {isDirty ? "You have unsaved changes." : "No pending changes."}
                        </div>
                        <Button
                            type="submit"
                            size="sm"
                            disabled={!canSave}
                            className="gap-1.5"
                            data-testid="profile-save-btn"
                        >
                            <FloppyDisk size={13} weight="fill" />
                            {saving ? "Saving…" : "Save profile"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
